import { UtensilsCrossed, X } from 'lucide-react'

import { t } from '../../../common/i18n.js'
import { Language } from '../../../common/types/i18n.js'
import { PageHeader } from '../../components/page-header/page-header.js'
import { PageShell } from '../../components/page-shell/page-shell.js'
import { Spinner } from '../../components/spinner/spinner.js'
import './eat.css'

export interface OrderPagination {
    page: number
    totalPages: number
    onPrev: () => void
    onNext: () => void
}

export interface EatViewProps {
    lang: Language
    title: string
    isLoading: boolean
    summaryCards: SummaryCard[]
    ordersTitle: string
    orderRows: OrderRow[]
    orderPagination: OrderPagination | null
    emptyMessage?: string
    ordering: OrderingSection
    payment: PaymentSection | null
}

export interface SummaryCard {
    label: string
    value: string
    variant?: 'default' | 'success' | 'warning'
}

export interface OrderRow {
    id: number
    date: string
    meal: string
    comment: string
    price: string
    status: string
    statusKey: string
    canDelete: boolean
}

export interface MealOption {
    id: number
    label: string
    price: string
}

export interface MealGroup {
    title: string
    meals: MealOption[]
}

export interface CommentOption {
    id: number
    label: string
    price: string
}

export interface CartItem {
    index: number
    mealLabel: string
    commentLabel: string | null
    price: string
}

export interface OrderingSection {
    title: string
    isBlocked: boolean
    blockedMessage: string
    noMealsMessage: string
    commentLabel: string
    noCommentLabel: string
    addToCartLabel: string
    placeOrderLabel: string
    cartTitle: string
    removeFromCartLabel: string
    isOrdering: boolean
    mealGroups: MealGroup[]
    comments: CommentOption[]
    selectedMealId: number | null
    selectedCommentId: number | null
    cart: CartItem[]
    onSelectMeal: (id: number) => void
    onSelectComment: (id: number | null) => void
    onAddToCart: () => void
    onRemoveFromCart: (index: number) => void
    onPlaceOrder: () => void
    onDeleteOrder: (id: number) => void
}

export interface PaymentSection {
    title: string
    ibanLabel: string
    iban: string
    payeeLabel: string
    payee: string
    markAsPaidLabel: string
    isPaying: boolean
    onPay: () => void
}

export const EatView = (props: EatViewProps) => {
    if (props.isLoading) {
        return (
            <PageShell>
                <PageHeader title={props.title} />
                <Spinner />
            </PageShell>
        )
    }

    return (
        <PageShell>
            <PageHeader title={props.title} />

            {/* Summary cards */}
            {props.summaryCards.length > 0 && (
                <div className="eat__summary">
                    {props.summaryCards.map((card) => (
                        <div key={card.label} className="eat__card">
                            <span className="eat__card-label">{card.label}</span>
                            <span
                                className={`eat__card-value${card.variant ? ` eat__card-value--${card.variant}` : ''}`}
                            >
                                {card.value}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* Ordering section */}
            <OrderingPanel ordering={props.ordering} />

            {/* Payment section */}
            {props.payment && <PaymentPanel payment={props.payment} />}

            {/* Orders table */}
            <div className="eat__section">
                <h2 className="eat__section-title">{props.ordersTitle}</h2>

                {props.orderRows.length === 0 ? (
                    <div className="eat__empty">
                        <div className="eat__empty-icon">
                            <UtensilsCrossed size={28} />
                        </div>
                        <div className="eat__empty-text">
                            {props.emptyMessage ?? t.eat.summary.noOrders(props.lang)}
                        </div>
                    </div>
                ) : (
                    <>
                        <table className="eat__table">
                            <thead>
                                <tr>
                                    <th>{t.eat.summary.date(props.lang)}</th>
                                    <th>{t.eat.summary.meal(props.lang)}</th>
                                    <th>{t.eat.summary.comment(props.lang)}</th>
                                    <th>{t.eat.summary.price(props.lang)}</th>
                                    <th>{t.eat.summary.status(props.lang)}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {props.orderRows.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.date}</td>
                                        <td>{row.meal}</td>
                                        <td>{row.comment}</td>
                                        <td className="eat__price">{row.price}</td>
                                        <td>
                                            <span
                                                className={`eat__status eat__status--${row.statusKey}`}
                                            >
                                                {row.status}
                                            </span>
                                        </td>
                                        <td>
                                            {row.canDelete && (
                                                <button
                                                    className="eat__delete-btn"
                                                    onClick={() =>
                                                        props.ordering.onDeleteOrder(row.id)
                                                    }
                                                    title={t.eat.deleteOrderTooltip(props.lang)}
                                                >
                                                    <X size={14} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {props.orderPagination && (
                            <div className="eat__pagination">
                                <button
                                    className="eat__page-btn"
                                    disabled={props.orderPagination.page <= 1}
                                    onClick={props.orderPagination.onPrev}
                                >
                                    {'<'}
                                </button>
                                <span className="eat__page-info">
                                    {props.orderPagination.page} /{' '}
                                    {props.orderPagination.totalPages}
                                </span>
                                <button
                                    className="eat__page-btn"
                                    disabled={
                                        props.orderPagination.page >=
                                        props.orderPagination.totalPages
                                    }
                                    onClick={props.orderPagination.onNext}
                                >
                                    {'>'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </PageShell>
    )
}

const OrderingPanel = ({ ordering }: { ordering: OrderingSection }) => {
    const hasAnyMeals = ordering.mealGroups.some((g) => g.meals.length > 0)

    return (
        <div className="eat__section eat__ordering">
            <h2 className="eat__section-title">{ordering.title}</h2>

            {ordering.isBlocked && (
                <div className="eat__order-blocked">{ordering.blockedMessage}</div>
            )}

            {!ordering.isBlocked && !hasAnyMeals && (
                <div className="eat__empty">
                    <div className="eat__empty-icon">
                        <UtensilsCrossed size={28} />
                    </div>
                    <div className="eat__empty-text">{ordering.noMealsMessage}</div>
                </div>
            )}

            {!ordering.isBlocked && hasAnyMeals && (
                <div className="eat__order-form">
                    {ordering.mealGroups
                        .filter((g) => g.meals.length > 0)
                        .map((group) => (
                            <div key={group.title} className="eat__meal-group">
                                <div className="eat__meal-group-title">{group.title}</div>
                                <div className="eat__meal-list">
                                    {group.meals.map((meal) => (
                                        <button
                                            key={meal.id}
                                            className={`eat__meal-option${ordering.selectedMealId === meal.id ? ' eat__meal-option--selected' : ''}`}
                                            onClick={() => ordering.onSelectMeal(meal.id)}
                                        >
                                            <span className="eat__meal-name">{meal.label}</span>
                                            <span className="eat__meal-price">{meal.price}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}

                    {ordering.comments.length > 0 && (
                        <div className="eat__comment-section">
                            <span className="eat__comment-label">{ordering.commentLabel}</span>
                            <div className="eat__comment-list">
                                <button
                                    className={`eat__comment-option${ordering.selectedCommentId === null ? ' eat__comment-option--selected' : ''}`}
                                    onClick={() => ordering.onSelectComment(null)}
                                >
                                    {ordering.noCommentLabel}
                                </button>
                                {ordering.comments.map((c) => (
                                    <button
                                        key={c.id}
                                        className={`eat__comment-option${ordering.selectedCommentId === c.id ? ' eat__comment-option--selected' : ''}`}
                                        onClick={() => ordering.onSelectComment(c.id)}
                                    >
                                        {c.label}
                                        {Number(c.price) > 0 && (
                                            <span className="eat__comment-price"> +{c.price}</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <button
                        className="eat__add-to-cart-btn"
                        disabled={ordering.selectedMealId === null}
                        onClick={ordering.onAddToCart}
                    >
                        {ordering.addToCartLabel}
                    </button>
                </div>
            )}

            {ordering.cart.length > 0 && (
                <div className="eat__cart">
                    <div className="eat__cart-title">{ordering.cartTitle}</div>
                    <div className="eat__cart-items">
                        {ordering.cart.map((item) => (
                            <div key={item.index} className="eat__cart-item">
                                <div className="eat__cart-item-details">
                                    <span className="eat__cart-item-meal">{item.mealLabel}</span>
                                    {item.commentLabel && (
                                        <span className="eat__cart-item-comment">
                                            + {item.commentLabel}
                                        </span>
                                    )}
                                </div>
                                <span className="eat__cart-item-price">{item.price}</span>
                                <button
                                    className="eat__delete-btn"
                                    onClick={() => ordering.onRemoveFromCart(item.index)}
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                    <button
                        className="eat__order-btn"
                        disabled={ordering.isOrdering}
                        onClick={ordering.onPlaceOrder}
                    >
                        {ordering.isOrdering
                            ? ordering.placeOrderLabel + '...'
                            : ordering.placeOrderLabel}
                    </button>
                </div>
            )}
        </div>
    )
}

const PaymentPanel = ({ payment }: { payment: PaymentSection }) => (
    <div className="eat__section eat__payment-section">
        <h2 className="eat__section-title">{payment.title}</h2>
        <div className="eat__payment-info">
            <div className="eat__payment-row">
                <span className="eat__payment-key">{payment.ibanLabel}</span>
                <span className="eat__payment-value eat__payment-iban">{payment.iban}</span>
            </div>
            <div className="eat__payment-row">
                <span className="eat__payment-key">{payment.payeeLabel}</span>
                <span className="eat__payment-value">{payment.payee}</span>
            </div>
        </div>
        <button className="eat__pay-btn" disabled={payment.isPaying} onClick={payment.onPay}>
            {payment.isPaying ? payment.markAsPaidLabel + '...' : payment.markAsPaidLabel}
        </button>
    </div>
)
